import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Users, Plus, Search, RefreshCw, Phone, Mail, Building2, Briefcase,
  User, BadgeCheck, IndianRupee, CheckCircle, Clock, XCircle, ChevronRight,
  CreditCard, UserPlus, TrendingUp, AlertCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import Layout from "../components/Layout";
import { API } from "../App";
import { toast } from "sonner";

const formatINR = (v) => {
  if (!v) return "₹0";
  const n = Number(v);
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)} L`;
  return `₹${n.toLocaleString("en-IN")}`;
};

const TYPE_LABELS = {
  existing_customer: "Existing Customer",
  employee: "Employee",
  channel_partner: "Channel Partner",
  other: "Other"
};

const TYPE_COLORS = {
  existing_customer: "bg-emerald-100 text-emerald-700",
  employee: "bg-blue-100 text-blue-700",
  channel_partner: "bg-purple-100 text-purple-700",
  other: "bg-slate-100 text-slate-700"
};

const COMMISSION_COLORS = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-blue-100 text-blue-700",
  paid: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-700"
};

const ReferralsPage = () => {
  const [tab, setTab] = useState("referrers");
  const [referrers, setReferrers] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [stats, setStats] = useState(null);
  const [pendingCommissions, setPendingCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  // Create referrer dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    type: "",
    name: "",
    phone: "",
    email: "",
    // Customer details
    project_name: "",
    unit_id: "",
    purchase_date: "",
    // Employee details
    employee_id: "",
    department: "",
    designation: "",
    // Other details
    relationship: "",
    company: "",
    // Bank details
    account_name: "",
    account_number: "",
    ifsc_code: "",
    bank_name: "",
    upi_id: "",
    notes: ""
  });

  // Commission action dialog
  const [commissionOpen, setCommissionOpen] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [commissionAction, setCommissionAction] = useState("");
  const [commissionForm, setCommissionForm] = useState({
    amount: "",
    payment_reference: "",
    notes: ""
  });
  const [processing, setProcessing] = useState(false);

  // Projects for dropdown
  const [projects, setProjects] = useState([]);

  // Fetch data
  const fetchReferrers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter) params.append("type", typeFilter);
      
      const [refRes, statsRes] = await Promise.all([
        axios.get(`${API}/referrals/referrers?${params}`),
        axios.get(`${API}/referrals/referrers/stats`)
      ]);
      setReferrers(refRes.data);
      setStats(statsRes.data);
    } catch (e) {
      toast.error("Failed to load referrers");
    } finally {
      setLoading(false);
    }
  };

  const fetchReferrals = async () => {
    try {
      const res = await axios.get(`${API}/referrals`);
      setReferrals(res.data);
    } catch (e) { /* ignore */ }
  };

  const fetchPendingCommissions = async () => {
    try {
      const res = await axios.get(`${API}/referrals/commission/pending`);
      setPendingCommissions(res.data);
    } catch (e) { /* ignore */ }
  };

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/projects`);
      setProjects(res.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    fetchReferrers();
    fetchReferrals();
    fetchPendingCommissions();
    fetchProjects();
  }, []);

  useEffect(() => {
    fetchReferrers();
  }, [typeFilter]);

  // Create referrer
  const handleCreateReferrer = async () => {
    if (!createForm.type || !createForm.name || !createForm.phone) {
      toast.error("Please fill required fields");
      return;
    }
    setCreating(true);
    try {
      const payload = {
        type: createForm.type,
        name: createForm.name,
        phone: createForm.phone,
        email: createForm.email || null,
        notes: createForm.notes || null
      };

      // Type-specific details
      if (createForm.type === "existing_customer") {
        payload.customer_details = {
          project_name: createForm.project_name || null,
          unit_id: createForm.unit_id || null,
          purchase_date: createForm.purchase_date || null
        };
      } else if (createForm.type === "employee") {
        payload.employee_details = {
          employee_id: createForm.employee_id || null,
          department: createForm.department || null,
          designation: createForm.designation || null
        };
      } else if (createForm.type === "other") {
        payload.other_details = {
          relationship: createForm.relationship || null,
          company: createForm.company || null
        };
      }

      // Bank details if provided
      if (createForm.account_number || createForm.upi_id) {
        payload.bank_details = {
          account_name: createForm.account_name || null,
          account_number: createForm.account_number || null,
          ifsc_code: createForm.ifsc_code || null,
          bank_name: createForm.bank_name || null,
          upi_id: createForm.upi_id || null
        };
      }

      await axios.post(`${API}/referrals/referrers`, payload);
      toast.success("Referrer added successfully");
      setCreateOpen(false);
      resetCreateForm();
      fetchReferrers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create referrer");
    }
    setCreating(false);
  };

  const resetCreateForm = () => {
    setCreateForm({
      type: "", name: "", phone: "", email: "",
      project_name: "", unit_id: "", purchase_date: "",
      employee_id: "", department: "", designation: "",
      relationship: "", company: "",
      account_name: "", account_number: "", ifsc_code: "", bank_name: "", upi_id: "",
      notes: ""
    });
  };

  // Commission actions
  const openCommissionDialog = (referral, action) => {
    setSelectedReferral(referral);
    setCommissionAction(action);
    setCommissionForm({
      amount: referral.commission_amount || "",
      payment_reference: "",
      notes: ""
    });
    setCommissionOpen(true);
  };

  const handleCommissionAction = async () => {
    setProcessing(true);
    try {
      const payload = {
        status: commissionAction,
        amount: commissionForm.amount ? parseFloat(commissionForm.amount) : null,
        payment_reference: commissionForm.payment_reference || null,
        notes: commissionForm.notes || null
      };
      await axios.put(`${API}/referrals/${selectedReferral.referral_id}/commission`, payload);
      toast.success(`Commission ${commissionAction}`);
      setCommissionOpen(false);
      fetchReferrals();
      fetchPendingCommissions();
      fetchReferrers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update commission");
    }
    setProcessing(false);
  };

  // Filter referrers by search
  const filteredReferrers = referrers.filter(r => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      r.name?.toLowerCase().includes(q) ||
      r.phone?.includes(q) ||
      r.email?.toLowerCase().includes(q)
    );
  });

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Referral Management</h1>
            <p className="text-sm text-slate-500 mt-1">Track referrers and commissions</p>
          </div>
          <Button onClick={() => setCreateOpen(true)} className="bg-slate-900 hover:bg-slate-800">
            <UserPlus className="w-4 h-4 mr-2" /> Add Referrer
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Total Referrers</p>
                    <p className="text-2xl font-bold text-slate-900">{stats.total_referrers}</p>
                  </div>
                  <Users className="w-8 h-8 text-slate-300" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-emerald-600 uppercase tracking-wide">Customers</p>
                    <p className="text-2xl font-bold text-emerald-700">{stats.by_type?.existing_customer || 0}</p>
                  </div>
                  <BadgeCheck className="w-8 h-8 text-emerald-300" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-blue-600 uppercase tracking-wide">Commission Paid</p>
                    <p className="text-2xl font-bold text-blue-700">{formatINR(stats.total_commission_paid)}</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-blue-300" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-amber-600 uppercase tracking-wide">Pending</p>
                    <p className="text-2xl font-bold text-amber-700">{formatINR(stats.total_commission_pending)}</p>
                  </div>
                  <Clock className="w-8 h-8 text-amber-300" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full h-auto flex overflow-x-auto gap-1 p-1">
            <TabsTrigger value="referrers" className="text-xs whitespace-nowrap px-3 py-2 flex-shrink-0">
              Referrers ({referrers.length})
            </TabsTrigger>
            <TabsTrigger value="referrals" className="text-xs whitespace-nowrap px-3 py-2 flex-shrink-0">
              Referrals ({referrals.length})
            </TabsTrigger>
            <TabsTrigger value="commissions" className="text-xs whitespace-nowrap px-3 py-2 flex-shrink-0">
              Pending Commissions ({pendingCommissions.length})
            </TabsTrigger>
          </TabsList>

          {/* Referrers Tab */}
          <TabsContent value="referrers" className="mt-4">
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by name, phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="existing_customer">Existing Customer</SelectItem>
                  <SelectItem value="employee">Employee</SelectItem>
                  <SelectItem value="channel_partner">Channel Partner</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : filteredReferrers.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No referrers found</p>
              </div>
            ) : (
              <div className="grid gap-3">
                {filteredReferrers.map(referrer => (
                  <Card key={referrer.referrer_id} className="bg-white hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-center gap-4">
                        <div className="flex items-center gap-3 flex-1">
                          <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                            <User className="w-5 h-5 text-slate-500" />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">{referrer.name}</p>
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                              <Phone className="w-3 h-3" /> {referrer.phone}
                              {referrer.email && (
                                <>
                                  <span className="text-slate-300">|</span>
                                  <Mail className="w-3 h-3" /> {referrer.email}
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        <Badge className={TYPE_COLORS[referrer.type]}>
                          {TYPE_LABELS[referrer.type]}
                        </Badge>

                        <div className="flex items-center gap-4 text-sm">
                          <div className="text-center">
                            <p className="font-semibold text-slate-900">{referrer.total_referrals}</p>
                            <p className="text-xs text-slate-500">Referrals</p>
                          </div>
                          <div className="text-center">
                            <p className="font-semibold text-emerald-600">{formatINR(referrer.total_commission_earned)}</p>
                            <p className="text-xs text-slate-500">Earned</p>
                          </div>
                          {referrer.pending_commission > 0 && (
                            <div className="text-center">
                              <p className="font-semibold text-amber-600">{formatINR(referrer.pending_commission)}</p>
                              <p className="text-xs text-slate-500">Pending</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Referrals Tab */}
          <TabsContent value="referrals" className="mt-4">
            {referrals.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No referrals yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {referrals.map(ref => (
                  <Card key={ref.referral_id} className="bg-white">
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-center gap-4">
                        <div className="flex-1">
                          <p className="font-semibold text-slate-900">{ref.lead_name}</p>
                          <p className="text-sm text-slate-500">{ref.project_name}</p>
                        </div>
                        <div className="text-sm">
                          <p className="text-slate-500">Referred by</p>
                          <p className="font-medium">{ref.referrer_name}</p>
                          <Badge className={`${TYPE_COLORS[ref.referrer_type]} text-xs mt-1`}>
                            {TYPE_LABELS[ref.referrer_type]}
                          </Badge>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold">{formatINR(ref.commission_amount)}</p>
                          <Badge className={COMMISSION_COLORS[ref.commission_status]}>
                            {ref.commission_status}
                          </Badge>
                        </div>
                        {ref.commission_status === "pending" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openCommissionDialog(ref, "approved")}
                          >
                            Approve
                          </Button>
                        )}
                        {ref.commission_status === "approved" && (
                          <Button
                            size="sm"
                            onClick={() => openCommissionDialog(ref, "paid")}
                            className="bg-emerald-600 hover:bg-emerald-700"
                          >
                            Mark Paid
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Pending Commissions Tab */}
          <TabsContent value="commissions" className="mt-4">
            {pendingCommissions.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No pending commissions</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pendingCommissions.map(ref => (
                  <Card key={ref.referral_id} className="bg-white border-l-4 border-l-amber-400">
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-center gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-slate-900">{ref.referrer_name}</p>
                            <Badge className={`${TYPE_COLORS[ref.referrer_type]} text-xs`}>
                              {TYPE_LABELS[ref.referrer_type]}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-500">
                            Lead: {ref.lead_name} • Stage: {ref.lead_stage}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-slate-900">{formatINR(ref.commission_amount)}</p>
                          <Badge className={COMMISSION_COLORS[ref.commission_status]}>
                            {ref.commission_status}
                          </Badge>
                        </div>
                        <div className="flex gap-2">
                          {ref.commission_status === "pending" && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openCommissionDialog(ref, "approved")}
                              >
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-200"
                                onClick={() => openCommissionDialog(ref, "cancelled")}
                              >
                                Cancel
                              </Button>
                            </>
                          )}
                          {ref.commission_status === "approved" && (
                            <Button
                              size="sm"
                              onClick={() => openCommissionDialog(ref, "paid")}
                              className="bg-emerald-600 hover:bg-emerald-700"
                            >
                              <CreditCard className="w-4 h-4 mr-1" /> Pay
                            </Button>
                          )}
                        </div>
                      </div>
                      {ref.bank_details && (ref.bank_details.account_number || ref.bank_details.upi_id) && (
                        <div className="mt-3 pt-3 border-t text-xs text-slate-500">
                          <span className="font-medium">Payment Info: </span>
                          {ref.bank_details.upi_id && <span>UPI: {ref.bank_details.upi_id} </span>}
                          {ref.bank_details.account_number && (
                            <span>A/C: {ref.bank_details.account_number} ({ref.bank_details.bank_name})</span>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Create Referrer Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Referrer</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Type Selection */}
              <div>
                <label className="text-sm font-medium text-slate-700">Referrer Type *</label>
                <Select value={createForm.type} onValueChange={(v) => setCreateForm({ ...createForm, type: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="existing_customer">Existing Customer</SelectItem>
                    <SelectItem value="employee">Employee</SelectItem>
                    <SelectItem value="channel_partner">Channel Partner</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700">Name *</label>
                  <Input
                    placeholder="Full name"
                    value={createForm.name}
                    onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Phone *</label>
                  <Input
                    placeholder="10 digit phone"
                    value={createForm.phone}
                    onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Email</label>
                <Input
                  type="email"
                  placeholder="email@example.com"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  className="mt-1"
                />
              </div>

              {/* Type-specific fields */}
              {createForm.type === "existing_customer" && (
                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-slate-700 mb-3">Customer Details</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-slate-500">Project Purchased</label>
                      <Select
                        value={createForm.project_name}
                        onValueChange={(v) => setCreateForm({ ...createForm, project_name: v })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select project" />
                        </SelectTrigger>
                        <SelectContent>
                          {projects.map(p => (
                            <SelectItem key={p.project_id} value={p.name}>{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">Unit Number</label>
                      <Input
                        placeholder="e.g., A-1201"
                        value={createForm.unit_id}
                        onChange={(e) => setCreateForm({ ...createForm, unit_id: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
              )}

              {createForm.type === "employee" && (
                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-slate-700 mb-3">Employee Details</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs text-slate-500">Employee ID</label>
                      <Input
                        placeholder="EMP001"
                        value={createForm.employee_id}
                        onChange={(e) => setCreateForm({ ...createForm, employee_id: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">Department</label>
                      <Input
                        placeholder="Sales"
                        value={createForm.department}
                        onChange={(e) => setCreateForm({ ...createForm, department: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">Designation</label>
                      <Input
                        placeholder="Manager"
                        value={createForm.designation}
                        onChange={(e) => setCreateForm({ ...createForm, designation: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
              )}

              {createForm.type === "other" && (
                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-slate-700 mb-3">Other Details</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-slate-500">Relationship</label>
                      <Select
                        value={createForm.relationship}
                        onValueChange={(v) => setCreateForm({ ...createForm, relationship: v })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Friend">Friend</SelectItem>
                          <SelectItem value="Family">Family</SelectItem>
                          <SelectItem value="Colleague">Colleague</SelectItem>
                          <SelectItem value="Business Associate">Business Associate</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">Company (if any)</label>
                      <Input
                        placeholder="Company name"
                        value={createForm.company}
                        onChange={(e) => setCreateForm({ ...createForm, company: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Bank Details */}
              <div className="border-t pt-4">
                <p className="text-sm font-medium text-slate-700 mb-3">Bank Details (for commission)</p>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-slate-500">UPI ID</label>
                    <Input
                      placeholder="name@upi"
                      value={createForm.upi_id}
                      onChange={(e) => setCreateForm({ ...createForm, upi_id: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-slate-500">Account Name</label>
                      <Input
                        placeholder="As per bank"
                        value={createForm.account_name}
                        onChange={(e) => setCreateForm({ ...createForm, account_name: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">Account Number</label>
                      <Input
                        placeholder="Account number"
                        value={createForm.account_number}
                        onChange={(e) => setCreateForm({ ...createForm, account_number: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-slate-500">IFSC Code</label>
                      <Input
                        placeholder="IFSC"
                        value={createForm.ifsc_code}
                        onChange={(e) => setCreateForm({ ...createForm, ifsc_code: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">Bank Name</label>
                      <Input
                        placeholder="Bank name"
                        value={createForm.bank_name}
                        onChange={(e) => setCreateForm({ ...createForm, bank_name: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <Textarea
                placeholder="Additional notes..."
                value={createForm.notes}
                onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateReferrer} disabled={creating}>
                {creating ? "Creating..." : "Add Referrer"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Commission Action Dialog */}
        <Dialog open={commissionOpen} onOpenChange={setCommissionOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {commissionAction === "approved" && "Approve Commission"}
                {commissionAction === "paid" && "Mark Commission Paid"}
                {commissionAction === "cancelled" && "Cancel Commission"}
              </DialogTitle>
            </DialogHeader>
            {selectedReferral && (
              <div className="space-y-4 py-4">
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="font-medium">{selectedReferral.referrer_name}</p>
                  <p className="text-sm text-slate-500">Lead: {selectedReferral.lead_name}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700">Commission Amount</label>
                  <Input
                    type="number"
                    value={commissionForm.amount}
                    onChange={(e) => setCommissionForm({ ...commissionForm, amount: e.target.value })}
                    className="mt-1"
                  />
                </div>

                {commissionAction === "paid" && (
                  <div>
                    <label className="text-sm font-medium text-slate-700">Payment Reference</label>
                    <Input
                      placeholder="Transaction ID / UTR"
                      value={commissionForm.payment_reference}
                      onChange={(e) => setCommissionForm({ ...commissionForm, payment_reference: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                )}

                <Textarea
                  placeholder="Notes (optional)"
                  value={commissionForm.notes}
                  onChange={(e) => setCommissionForm({ ...commissionForm, notes: e.target.value })}
                  rows={2}
                />
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setCommissionOpen(false)}>Cancel</Button>
              <Button
                onClick={handleCommissionAction}
                disabled={processing}
                className={
                  commissionAction === "cancelled" ? "bg-red-600 hover:bg-red-700" :
                  commissionAction === "paid" ? "bg-emerald-600 hover:bg-emerald-700" :
                  ""
                }
              >
                {processing ? "Processing..." : 
                  commissionAction === "approved" ? "Approve" :
                  commissionAction === "paid" ? "Confirm Payment" :
                  "Cancel Commission"
                }
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ReferralsPage;
